import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Divider, FormControl, FormControlLabel, IconButton, InputLabel, Link,
  MenuItem, Select, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { ContentCopyOutlined, RotateRightOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import { ShallowFormDisplay } from '../../../presentation-components/form-field-components/general/shallow-form-display';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../../shared/services/survey/survey-admin.service';
import { IUpdateSurvey, SurveyDistributionMode } from '../../../../shared/services/survey/survey.model';
import { SurveyHubTabProps } from '../survey-hub-page';

const ACCESS_OPTIONS: { value: SurveyDistributionMode; label: string }[] = [
  { value: 'INVITED_ONLY', label: 'Invite links only' },
  { value: 'OPEN_LINK_ONLY', label: 'Open link only' },
  { value: 'BOTH', label: 'Invite links and open link' },
];

interface SettingsForm {
  name: string;
  description: string;
  distributionMode: SurveyDistributionMode;
  allowMultiple: boolean;
  expiresAt: string;
}

interface Props extends SurveyHubTabProps {
  onArchive: () => void;
  onActivate: () => void;
  statusBusy: boolean;
}

export const SurveyOverviewTab = ({ survey, reload, goToTab, onArchive, onActivate, statusBusy }: Props) => {
  const navigate = useNavigate();
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [form, setForm] = useState<SettingsForm>({
    name: survey.name,
    description: survey.description ?? '',
    distributionMode: survey.distributionMode,
    allowMultiple: survey.allowMultipleSubmissions,
    expiresAt: survey.expiresAt ? moment(survey.expiresAt).format('YYYY-MM-DD') : '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isRotating, setIsRotating] = useState(false);

  useEffect(() => {
    setForm({
      name: survey.name,
      description: survey.description ?? '',
      distributionMode: survey.distributionMode,
      allowMultiple: survey.allowMultipleSubmissions,
      expiresAt: survey.expiresAt ? moment(survey.expiresAt).format('YYYY-MM-DD') : '',
    });
  }, [survey]);

  const isProductData = survey.purpose === 'SUPPLIER_PRODUCT_DATA';

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload: IUpdateSurvey = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        expiresAt: form.expiresAt || undefined,
        ...(isProductData ? {} : {
          distributionMode: form.distributionMode,
          allowMultipleSubmissions: form.allowMultiple,
        }),
      };
      await surveyAdminService.updateSurvey(survey.id, payload);
      userNotificationsContext.openSnackbar({ open: true, message: 'Survey updated', backgroundColor: 'primary.dark' });
      reload();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to save', backgroundColor: 'text.primary' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Delete survey',
      message: `This deletes "${survey.name}" along with its recipients and responses. This cannot be undone.`,
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
    }).then(async confirmed => {
      if (!confirmed) return;
      try {
        await surveyAdminService.deleteSurvey(survey.id);
        userNotificationsContext.openSnackbar({ open: true, message: 'Survey deleted', backgroundColor: 'primary.dark' });
        navigate('/surveys');
      } catch {
        userNotificationsContext.openSnackbar({ open: true, message: 'Failed to delete survey', backgroundColor: 'text.primary' });
      }
    });
  };

  const handleRotateToken = async () => {
    setIsRotating(true);
    try {
      await surveyAdminService.rotateOpenLinkToken(survey.id);
      userNotificationsContext.openSnackbar({ open: true, message: 'A new open link has been generated. The old link no longer works.', backgroundColor: 'primary.dark' });
      reload();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to generate a new link', backgroundColor: 'text.primary' });
    } finally {
      setIsRotating(false);
    }
  };

  const openLinkUrl = survey.openLinkToken
    ? `${window.location.origin}/survey-respond?openLinkToken=${survey.openLinkToken}`
    : null;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      userNotificationsContext.openSnackbar({ open: true, message: 'Copied to clipboard', backgroundColor: 'primary.dark' });
    });
  };

  const showOpenLink = !isProductData && (form.distributionMode === 'OPEN_LINK_ONLY' || form.distributionMode === 'BOTH');

  return (
    <Box>
      <Stack spacing={3} sx={{ maxWidth: 640 }}>
        <TextField
          label='Survey name'
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          fullWidth
        />
        <TextField
          label='Description (optional)'
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          fullWidth
          multiline
          rows={2}
        />
        {!isProductData && (
          <FormControl fullWidth>
            <InputLabel>Access</InputLabel>
            <Select
              value={form.distributionMode}
              label='Access'
              onChange={e => setForm(f => ({ ...f, distributionMode: e.target.value as SurveyDistributionMode }))}
            >
              {ACCESS_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {!isProductData && (
          <FormControlLabel
            control={(
              <Switch
                checked={form.allowMultiple}
                onChange={e => setForm(f => ({ ...f, allowMultiple: e.target.checked }))}
              />
            )}
            label='Allow multiple submissions per respondent'
          />
        )}
        <TextField
          label='Expires at (optional)'
          type='date'
          value={form.expiresAt}
          onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />

        <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
          <LoadingButton
            variant='contained'
            loading={isSaving}
            onClick={handleSave}
            disabled={!form.name.trim()}
          >
            Save changes
          </LoadingButton>
          {survey.status !== 'ACTIVE' && (
            <Button variant='outlined' disabled={statusBusy} onClick={onActivate}>
              {survey.status === 'ARCHIVED' ? 'Reopen survey' : 'Activate survey'}
            </Button>
          )}
        </Stack>
      </Stack>

      {showOpenLink && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant='h6' sx={{ mb: 1 }}>Open link</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Anyone with this link can respond while the survey is active.
          </Typography>
          {openLinkUrl ? (
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='body2' sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                {openLinkUrl}
              </Typography>
              <Tooltip title='Copy link'>
                <IconButton onClick={() => copyToClipboard(openLinkUrl)}>
                  <ContentCopyOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title='Generate a new link (the current link stops working)'>
                <span>
                  <IconButton onClick={handleRotateToken} disabled={isRotating}>
                    <RotateRightOutlined />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ) : (
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography color='text.secondary' variant='body2'>No open link generated yet.</Typography>
              <Button size='small' variant='outlined' onClick={handleRotateToken} disabled={isRotating}>Generate</Button>
            </Stack>
          )}
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Typography variant='h6' sx={{ mb: 2 }}>Details</Typography>
        <ShallowFormDisplay
          name='Survey type'
          value={isProductData ? 'Product data request' : 'Question survey'}
          isTopRow
        />
        {!isProductData && (
          <ShallowFormDisplay
            name='Question set'
            value={survey.surveyQuestionGroup?.name
              ? (
                <Link
                  component='button'
                  type='button'
                  underline='hover'
                  onClick={() => goToTab('questions')}
                  sx={{ cursor: 'pointer' }}
                >
                  {survey.surveyQuestionGroup.name}
                </Link>
              )
              : '—'}
          />
        )}
        <ShallowFormDisplay name='Created' value={moment(survey.createdAt).format('DD/MM/YYYY HH:mm')} />
        <ShallowFormDisplay name='Last updated' value={moment(survey.updatedAt).format('DD/MM/YYYY HH:mm')} isBottomRow />
      </Box>

      {/* Destructive and closing actions live at the bottom, away from Save. */}
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Stack direction='row' spacing={2} justifyContent='flex-end' flexWrap='wrap' useFlexGap>
          {survey.status === 'ACTIVE' && (
            <Button variant='outlined' disabled={statusBusy} onClick={onArchive}>
              Archive survey
            </Button>
          )}
          <Button variant='outlined' color='error' onClick={handleDelete}>
            Delete survey
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
